/**
 * Copy contract for the remaining redesign screens (#950): wrap-up (W),
 * parts/supplies (P), points (Y) and city reports (C).
 *
 * Same pattern as `redesign-copy.ts` and `redesign-wizard-copy.ts` — the
 * wording that carries a promise lives in one tested place, because these
 * are exactly the strings the audit caught lying:
 *
 *   - the wrap-up promised a message the office had no way to send (fixed
 *     for real by #815, so the promise is honest now — but it must stay
 *     worded as the conditional it is);
 *   - the supply list claimed "closest to me" while sorting A→Z (#799);
 *   - points and the reports tab expected a manual nobody reads.
 */

/* ── W6: what "Send" actually does ──────────────────────────────────────── */

export interface SendExplainer {
    /** What happens the moment the tech taps Send. */
    whatHappens: string;
    /** Whether anything comes back to them, stated conditionally. */
    whatComesBack: string;
}

/**
 * Explains Office review BEFORE the tech meets that status (SPEC W6, and the
 * mirror of the job page's done-receipt in #948).
 *
 * The "they'll message you only if something's missing" half is a promise
 * the office can now keep — #815 gave close-out a channel back to the tech.
 * Before that it was a lie the wrap-up screen told, and the close-out audit
 * gated the weekend flip on it.
 */
export function sendExplainer(): SendExplainer {
    return {
        whatHappens: "Your paperwork goes to the office. They check it and file the report with the city.",
        whatComesBack: "You'll only hear back if something's missing — otherwise this job is finished for you.",
    };
}

/* ── P6: the sort origin must never lie ─────────────────────────────────── */

export type SupplySortOrigin = "me" | "next_job" | "alphabetical";

export interface SortOriginCopy {
    /** The label on the control. */
    label: string;
    /** What the list is ACTUALLY sorted by, said out loud. */
    truth: string;
    /** Present when the tech can do something about it. */
    action: string | null;
}

/**
 * The honest description of how the supply list is sorted (SPEC P5/P6).
 *
 * The bug (#799): with no GPS fix the list still said "Closest to me" while
 * silently sorting A→Z, so a tech drove past a nearer branch trusting a
 * label. The rule now: when we don't have a location we SAY we're showing
 * A→Z and offer the retry — a wrong distance is worse than an honest one.
 */
export function sortOriginCopy(origin: SupplySortOrigin): SortOriginCopy {
    switch (origin) {
        case "me":
            return {
                label: "Closest to me",
                truth: "Sorted by how far each branch is from where you are now.",
                action: null,
            };
        case "next_job":
            return {
                label: "Closest to next job",
                truth: "Sorted by how far each branch is from your next stop.",
                action: null,
            };
        case "alphabetical":
            return {
                label: "A→Z",
                // Never claim a distance order we can't compute.
                truth: "We can't get your location right now, so this is in alphabetical order — not by distance.",
                action: "Try my location again",
            };
    }
}

/* ── P2/P3: the focus-day rule, said out loud ───────────────────────────── */

/** "1 of 3 parts on your truck" (P3) — plain, never a bare fraction. */
export function partsProgress(onTruck: number, total: number): string {
    if (total <= 0) return "No parts to load for this day.";
    return `${onTruck} of ${total} part${total === 1 ? "" : "s"} on your truck`;
}

/**
 * Why this list shows one day at a time (P2), and — when it jumps ahead —
 * why it did. The audit found techs seeing a later day's parts with no
 * explanation and assuming the list was broken.
 */
export function focusDayNote(opts: { isToday: boolean; dayLabel: string }): string {
    return opts.isToday
        ? "This is today's list. One day at a time, so nothing gets loaded early by mistake."
        : `Nothing to load today — showing ${opts.dayLabel} instead, your next day with parts.`;
}

/* ── Y2: points explain themselves on day one ───────────────────────────── */

export interface PointsExplainer {
    what: string;
    pending: string;
}

/**
 * What points are and what "pending" means (SPEC Y1/Y2), on the screen
 * rather than in a manual. Money rule: this explains points WITHOUT ever
 * naming a dollar figure — techs see points and PAID/UNPAID only.
 */
export function pointsExplainer(): PointsExplainer {
    return {
        what: "Points are how your work gets counted. Every job you finish is worth points based on what it took.",
        pending:
            "Pending means the job is done but the office hasn't finished checking it yet. It moves to earned once they do.",
    };
}

/* ── C1/C2: the city-reports tab says what it is ────────────────────────── */

export interface ReportsTabCopy {
    /** The tab's own subtitle — what this list actually holds. */
    subtitle: string;
    /** The empty state, which must teach rather than shame (doctrine 6). */
    emptyTitle: string;
    emptyBody: string;
}

/**
 * City Reports identity + a teaching empty state (SPEC C1/C2).
 *
 * The doctrine line that keeps this tab honest: it holds ONLY the licensed
 * tester-entry chore. Everything else about reports — generation, the paper
 * lane, city returns, the archive — is the office's, so an empty list is the
 * normal case and says so instead of implying the tech forgot something.
 */
export function reportsTabCopy(): ReportsTabCopy {
    return {
        subtitle: "Tests you still need to type into the city's website",
        emptyTitle: "Nothing waiting on you",
        emptyBody:
            "Most cities are the office's job. Only the ones that require your tester license land here — so an empty list is normal, not something you missed.",
    };
}

/* ── C3: the queue row explains why it is YOURS ─────────────────────────── */

/**
 * Why this one is the tech's and not the office's (SPEC C3).
 *
 * The audit's finding: a tester looked at this list and read it as "reports
 * the office hasn't done yet" — work someone else was slacking on. It isn't.
 * These cities require the *licensed tester* to type the results in; the
 * office is not allowed to. Saying that on the row turns a nag into a reason.
 */
export function whyThisIsYours(cityName: string | null | undefined): string {
    const city = (cityName ?? "").trim();
    return city
        ? `${city} makes the tester type the results in. It rides on your license, so the office can't do this one.`
        : "This city makes the tester type the results in. It rides on your license, so the office can't do this one.";
}

/**
 * The age chip, in units a person uses (SPEC C3: "honest age chip").
 * Hours read fine on day one and stop meaning anything by day three, so past
 * 48 hours we count days. Null when we have no test time to be honest about.
 */
export function ageChipLabel(hours: number | null | undefined): string | null {
    if (hours == null || !Number.isFinite(hours) || hours < 0) return null;
    if (hours < 1) return "tested just now";
    if (hours < 48) return `tested ${Math.floor(hours)}h ago`;
    const days = Math.floor(hours / 24);
    return `tested ${days} days ago`;
}

/** The oldest row carries the reason it's on top — the list is not random. */
export const OLDEST_FIRST_NOTE = "This one's been waiting longest, so it's on top.";

/* ── C4: portal vs paper, which this city actually is ───────────────────── */

export function cityFilingIdentity(opts: { isPortal: boolean; platform: string | null | undefined }): string {
    const platform = (opts.platform ?? "").trim();
    if (opts.isPortal) return platform ? `their website: ${platform}` : "their website — an online portal";
    return "this city takes paper — the office mails it, not you";
}

/* ── C6: the receipt, honest about what is and isn't confirmed ──────────── */

export interface SentReceipt {
    headline: string;
    body: string;
}

/**
 * What the tech sees the moment they mark one filed (SPEC C6).
 *
 * Deliberately does NOT claim the city confirmed anything: nothing in the
 * app writes the `filed` state today (report-input Phase 2), so a "city
 * confirmed" receipt would be a lie. It says what is true — it's off the
 * tech's list, and the office watches for the city's confirmation.
 */
export function sentReceipt(cityName: string | null | undefined, siteName: string | null | undefined): SentReceipt {
    const city = (cityName ?? "").trim();
    const site = (siteName ?? "").trim() || "That job";
    return {
        headline: city ? `Sent to ${city}` : "Sent",
        body: `${site} is off your list. The office watches for the city's confirmation — you're done with it.`,
    };
}

/* ── C2: the teaching empty state, in full ──────────────────────────────── */

/**
 * The long-form empty state (SPEC C2). `reportsTabCopy().emptyBody` is the
 * one-liner for tight spaces; this is what the tab itself shows, because the
 * question a green tester actually has is "why do only SOME cities land here?"
 */
export const REPORTS_EMPTY_PARAGRAPHS: string[] = [
    "Some cities make you — the tester — type test results into their own website. The office isn't allowed to; it rides on your license.",
    "When one of your tests needs that, it shows up here with everything you need to copy over.",
    "Most cities don't work this way — the office files those. So this list being empty is normal.",
];

/* ── Y3: every bar says its day and its number ──────────────────────────── */

export interface PointsBarLabel {
    /** "Mon", or "TODAY" for the current day. */
    dayLabel: string;
    /** The number under the bar — "off" on a day with no work booked. */
    valueLabel: string;
    isToday: boolean;
}

/**
 * The chart label for one day (SPEC Y3). A zero on a day the tech didn't
 * work reads as a bad day unless we say it was a day off, so a zero with no
 * jobs says "off" rather than "0".
 */
export function pointsBarLabel(opts: {
    dayLabel: string;
    points: number;
    isToday: boolean;
    isFuture: boolean;
    worked: boolean;
}): PointsBarLabel {
    const valueLabel = opts.isFuture ? "" : opts.points > 0 ? String(opts.points) : opts.worked ? "0" : "off";
    return { dayLabel: opts.isToday ? "TODAY" : opts.dayLabel, valueLabel, isToday: opts.isToday };
}

/* ── Y4/Y5: traceability, and the way to ask about it ───────────────────── */

export type PointsJobState = "earned" | "pending" | "up_next";

/** The plain-words state of one job's points (SPEC Y4). */
export function pointsJobStateCopy(state: PointsJobState): { chip: string; note: string } {
    switch (state) {
        case "earned":
            return { chip: "earned", note: "checked by the office — already in your total" };
        case "pending":
            return { chip: "pending", note: "done, waiting for the office to check it" };
        case "up_next":
            return { chip: "up next", note: "not pending yet — points land when you send it" };
    }
}

/**
 * The ask path (SPEC Y5). The point is that the tech never has to explain
 * WHICH job — the app sends it. Two days is the threshold the office quoted
 * for a normal review turnaround.
 */
export const POINTS_ASK_PROMPT =
    "If a pending job takes longer than 2 days, or a number looks wrong, tap here to ask the office — it sends them the exact job.";
export const POINTS_ASK_LABEL = "Ask about a job's points";

/** The message body the ask sends, so the office gets the job, not a riddle. */
export function pointsAskMessage(opts: { jobLabel: string; points: number; state: PointsJobState }): string {
    const { chip } = pointsJobStateCopy(opts.state);
    return `Question about my points on ${opts.jobLabel} — showing ${opts.points} pt${opts.points === 1 ? "" : "s"} (${chip}).`;
}

/* ── W1/W2/W4/W5: the wrap-up screen (#999) ─────────────────────────────── */

export interface WrapUpRecap {
    /** "Wrap up — Oak Ridge Dental" */
    headline: string;
    /** "Both devices tested · 2 passed · 6 photos taken along the way" */
    line: string;
}

/**
 * W1 — the job recapped in numbers, at the top of the wrap-up.
 *
 * The audit's finding: the tech reaches the last screen and has no idea what
 * they are about to send. Counting it back to them ("both devices tested, 2
 * passed") is the difference between signing something and signing something
 * you can see.
 *
 * "Both" rather than "2 of 2" when the whole job is done, because that is the
 * sentence a person says out loud.
 *
 * `devicesTotal` is the devices this job REQUIRES (the device list's
 * "REQUIRED (n)"), never every device at the site — see `submitGateDeviceCount`.
 */
export function wrapUpRecap(opts: {
    customerName?: string | null;
    devicesTested: number;
    devicesTotal: number;
    passed: number;
    photos: number;
}): WrapUpRecap {
    const customer = (opts.customerName ?? "").trim();
    const headline = customer ? `Wrap up — ${customer}` : "Wrap up";

    const allDone = opts.devicesTotal > 0 && opts.devicesTested >= opts.devicesTotal;
    const devicePart =
        opts.devicesTotal === 0
            ? "No devices on this job"
            : allDone && opts.devicesTotal === 1
              ? "Device tested"
              : allDone && opts.devicesTotal === 2
                ? "Both devices tested"
                : allDone
                  ? `All ${opts.devicesTotal} devices tested`
                  : `${opts.devicesTested} of ${opts.devicesTotal} devices tested`;

    const failed = Math.max(0, opts.devicesTested - opts.passed);
    const resultPart =
        opts.devicesTested === 0
            ? null
            : failed === 0
              ? `${opts.passed === 1 ? "it" : "all"} passed`
              : opts.passed === 0
                ? `${failed === 1 ? "it" : "all"} failed`
                : `${opts.passed} passed, ${failed} failed`;

    const photoPart =
        opts.photos > 0 ? `${opts.photos} photo${opts.photos === 1 ? "" : "s"} taken along the way` : null;

    return { headline, line: [devicePart, resultPart, photoPart].filter(Boolean).join(" · ") };
}

/** The three steps of wrapping up, in the order the screen runs them. */
export const WRAP_UP_STEPS = ["Work details", "Signatures", "Check & send"] as const;
export type WrapUpStep = (typeof WRAP_UP_STEPS)[number];

export interface WrapUpStepChip {
    label: WrapUpStep;
    position: number;
    state: "done" | "current" | "todo";
}

/** W1 — the path, so the last screen isn't a surprise either. */
export function wrapUpStepChips(current: WrapUpStep): WrapUpStepChip[] {
    const idx = WRAP_UP_STEPS.indexOf(current);
    return WRAP_UP_STEPS.map((label, i) => ({
        label,
        position: i + 1,
        state: i < idx ? "done" : i === idx ? "current" : "todo",
    }));
}

/**
 * W2 — the signature is already there, and the screen finally says so.
 *
 * It has pre-filled from the tech's saved default for a while; the screen
 * showed the image with a bare "Sign manually" button and never explained
 * where it came from, so techs re-drew it every job just in case.
 */
export const SIGNATURE_ALREADY_HERE = "Saved from your profile. Tap the pad only if you want to re-sign.";
export const SIGNATURE_NONE_YET =
    "Sign once here, then tap “Save as my signature” — after that it fills itself in on every job.";

/** The lines the signature files under, shown as filled-for-you facts. */
export function signatureFacts(opts: { name?: string | null; dateLabel: string; siteLabel?: string | null }): Array<{
    label: string;
    value: string;
}> {
    const rows: Array<{ label: string; value: string }> = [];
    const name = (opts.name ?? "").trim();
    if (name) rows.push({ label: "Name", value: name });
    rows.push({ label: "Date", value: opts.dateLabel });
    const site = (opts.siteLabel ?? "").trim();
    if (site) rows.push({ label: "Where", value: site });
    return rows;
}

export interface CheckLine {
    key: string;
    label: string;
    /** Never empty — a "—" row on a review screen reads as lost data. */
    value: string;
    /** Which step a "change" tap should reopen; null = nothing to change. */
    change: "devices" | "photos" | "signature" | null;
}

/**
 * W4 — "Check & send": what the app already worked out, as readable lines.
 *
 * The rule that shapes this: **never render a "—" row.** The audit found
 * review screens full of blank dashes, and a tech reads a dash as "the app
 * lost my work". Every line here either states a real value or says plainly
 * that nothing of that kind happened.
 */
export function checkAndSendLines(opts: {
    devicesTested: number;
    passed: number;
    failed: number;
    flaggedForRepair: number;
    photos: number;
    shutoffsReopened: boolean | null;
}): CheckLine[] {
    const lines: CheckLine[] = [];

    lines.push({
        key: "what",
        label: "What was done",
        value:
            opts.devicesTested === 0
                ? "Nothing tested yet"
                : `Tested ${opts.devicesTested} backflow${opts.devicesTested === 1 ? "" : "s"} — ${
                      opts.failed === 0
                          ? opts.devicesTested === 1
                              ? "it passed"
                              : "all passed"
                          : `${opts.passed} passed, ${opts.failed} failed`
                  }`,
        change: "devices",
    });

    lines.push({
        key: "fail",
        label: "Any device fail?",
        value: opts.failed === 0 ? "No — none failed" : `Yes — ${opts.failed} did`,
        change: "devices",
    });

    lines.push({
        key: "repair",
        label: "Anything flagged for repair?",
        value:
            opts.flaggedForRepair === 0
                ? "No — nothing flagged"
                : `Yes — ${opts.flaggedForRepair} flagged for the office`,
        change: "devices",
    });

    lines.push({
        key: "water",
        label: "Water back on when you left?",
        value:
            opts.shutoffsReopened === true
                ? "Yes — shutoffs reopened"
                : opts.shutoffsReopened === false
                  ? "No — you left a shutoff closed, and said so"
                  : "Not recorded on this job",
        change: "devices",
    });

    lines.push({
        key: "photos",
        label: "Photos",
        value: opts.photos === 0 ? "None yet" : `${opts.photos} — each one named`,
        change: "photos",
    });

    return lines;
}

export interface ChecklistItem {
    key: string;
    label: string;
    state: "done" | "missing" | "skipped";
    /** Present when the state needs explaining (a skip always does). */
    note: string | null;
}

/**
 * W5 — the final checklist, DERIVED rather than asked.
 *
 * Every row is something the app already knows. Asking the tech to tick
 * "photos in?" when the app is holding the photos is how a checklist becomes
 * a thing people tick without reading.
 *
 * The devices row counts the devices this job REQUIRES, and when the caller
 * knows the submit gate's verdict (`devicesClear`) that verdict decides the
 * row: amber only when the gate would actually hold the job. An amber
 * "9 still to go" on a job the server called ready made a tech think he was
 * blocked (#092678199).
 */
export function wrapUpChecklist(opts: {
    devicesTested: number;
    devicesTotal: number;
    /** The submit gate's device verdict (server `ready`, or the same rule run locally). */
    devicesClear?: boolean;
    photosMissing: number;
    hasSignature: boolean;
}): ChecklistItem[] {
    const items: ChecklistItem[] = [];

    const remaining = Math.max(0, opts.devicesTotal - opts.devicesTested);
    const allTested = opts.devicesClear ?? (opts.devicesTotal > 0 && remaining === 0);
    items.push({
        key: "devices",
        label: "Every required device tested and recorded",
        state: allTested ? "done" : "missing",
        note: allTested || remaining === 0 ? null : `${remaining} still to go`,
    });

    items.push({
        key: "photos",
        label: "All required photos in",
        state: opts.photosMissing === 0 ? "done" : "missing",
        note: opts.photosMissing === 0 ? null : `${opts.photosMissing} still needed`,
    });

    items.push({
        key: "signature",
        label: "Your signature on it",
        state: opts.hasSignature ? "done" : "missing",
        note: opts.hasSignature ? null : "Sign above before you send",
    });

    return items;
}

/* ── C7: the tester's own "I'll file this one" ──────────────────────────── */

/**
 * C7 — the tech-initiated self-queue.
 *
 * Deliberately NOT an auto-add. There is no "this city makes the tester file
 * it" attribute anywhere — `is_portal` means the city uses a portal, not that
 * the tester is the one who must type it — so auto-queuing would quietly move
 * the office's work onto the tech, which is the exact thing the City Reports
 * doctrine forbids. This is the explicit human action the endpoint's own
 * docstring describes.
 */
export const SELF_QUEUE_PROMPT =
    "Is this a city where you file the report yourself? Add it to your City Reports list so it isn't forgotten.";
export const SELF_QUEUE_LABEL = "I'll file this one online";
export const SELF_QUEUE_DONE = "Added to your City Reports list. It'll be waiting when you've got signal.";
/**
 * The TICKED-but-not-yet-sent state. Deliberately future tense: at the moment
 * the box is ticked nothing has been added — the call only fires once the
 * submit lands — and a screen that says "Added" before it is added is the
 * exact kind of lie this redesign exists to remove.
 */
export const SELF_QUEUE_PENDING = "It'll drop into your City Reports list as soon as this job sends.";
